import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateCircle, setCurrentStep } from "reducer/index";
import linesConfigurationOptions from "db/line_configuration_options.json";
import "./ConfigurationPanel.scss";
import SelectConfig from "./select-config/SelectConfig";
import Icon from "@mdi/react";
import { mdiChevronDown } from "@mdi/js";

const ConfigurationPanel = () => {
  const dispatch = useDispatch();
  const circles = useSelector(({ circles }) => circles);
  const selects = linesConfigurationOptions["hydraulic"];

  // State per ogni linea idraulica
  const [linesConfig, setLinesConfig] = useState({});

  useEffect(() => {
    // Inizializza configurazioni esistenti
    const initialConfig = {};
    circles
      .filter((c) => c?.type === "hydraulic")
      .forEach((circle) => {
        initialConfig[circle.id] = {
          ...circle,
          setting: circle.setting || {},
        };
      });
    setLinesConfig(initialConfig);
  }, [circles]);

  const hydraulicCircles = circles.filter((c) => c?.type === "hydraulic");

  const setActiveConfig = (circleId, target, value, extra) => {
    const newConfig = {
      ...linesConfig[circleId],
      setting: { ...linesConfig[circleId].setting, [target]: value },
    };
    if (extra) {
      newConfig.setting = {
        ...newConfig.setting,
        [extra.target]: {
          ...extra.data,
          reference: {
            target: target,
            data: {
              value: value.value,
              label: value.label,
            },
          },
        },
      };
    }
    setLinesConfig({
      ...linesConfig,
      [circleId]: newConfig,
    });
  };

  const resetActiveConfig = (circleId, target, extra) => {
    const newConfig = {
      ...linesConfig[circleId],
      setting: { ...linesConfig[circleId]?.setting, [target]: null },
    };
    if (extra) {
      newConfig.setting = {
        ...newConfig.setting,
        [extra.target]: null,
      };
    }
    setLinesConfig({
      ...linesConfig,
      [circleId]: newConfig,
    });
  };

  const copySettings = (fromCircleId, toCircleId) => {
    const fromConfig = linesConfig[fromCircleId];
    if (fromConfig && fromConfig.setting) {
      setLinesConfig({
        ...linesConfig,
        [toCircleId]: {
          ...linesConfig[toCircleId],
          setting: { ...fromConfig.setting },
        },
      });
    }
  };

  const getValue = (circleId, select) => {
    const config = linesConfig[circleId];
    if (!config || !config.setting) return null;

    if (select.id === "port") {
      return config.setting[select.id]
        ? `${config.setting[select.id].value}"`
        : null;
    }
    return config.setting[select.id]?.label || null;
  };

  const isLineComplete = (circleId) => {
    const config = linesConfig[circleId];
    if (!config || !config.setting) return false;
    return selects.every((select) => !!config.setting[select.id]);
  };

  const allLinesComplete = hydraulicCircles.every((circle) =>
    isLineComplete(circle.id)
  );

  const handleContinue = () => {
    // Salva tutte le configurazioni
    hydraulicCircles.forEach((circle) => {
      if (linesConfig[circle.id] && linesConfig[circle.id].setting) {
        dispatch(updateCircle(circle.id, linesConfig[circle.id].setting));
      }
    });
    dispatch(setCurrentStep(3));
  };

  return (
    <div className="configuration-panel">
      <h2>Configure Hydraulic Lines</h2>

      <div className="lines-list">
        {hydraulicCircles.map((circle, index) => (
          <div
            key={circle.id}
            className={`line-config ${
              isLineComplete(circle.id) ? "complete" : ""
            }`}
          >
            <div className="line-header">
              <h3>Hydraulic Line {index + 1}</h3>
              {index > 0 && (
                <button
                  className="btn-copy"
                  onClick={() =>
                    copySettings(hydraulicCircles[index - 1].id, circle.id)
                  }
                  title="Copy settings from previous line"
                >
                  <span>Copy configuration</span>
                  <Icon path={mdiChevronDown} size={0.8} />
                </button>
              )}
            </div>

            <div className="line-selects">
              {selects.map((select, i) => (
                <SelectConfig
                  key={`${circle.id}_select_${i}`}
                  value={getValue(circle.id, select)}
                  target={select.id}
                  options={select.options}
                  isAlreadySetted={false}
                  setActiveConfig={(target, value, extra) =>
                    setActiveConfig(circle.id, target, value, extra)
                  }
                  resetActiveConfig={(target, extra) =>
                    resetActiveConfig(circle.id, target, extra)
                  }
                  tempConfig={linesConfig[circle.id] || {}}
                  type="hydraulic"
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="action-buttons">
        <button
          className="btn btn-outline"
          onClick={() => dispatch(setCurrentStep(1))}
        >
          Back
        </button>
        <button
          className="btn btn-primary"
          onClick={handleContinue}
          disabled={!allLinesComplete}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default ConfigurationPanel;
